import { useEffect, useState } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { RECALL_EVENT_COPY } from '#app/routes/mocra+/_form+/components/recall-events/constants.ts'
import { RecallEventForm } from '#app/routes/mocra+/_form+/components/recall-events/form.tsx'
import {
	collectRecallEventErrors,
	createRecallEventDraft,
	sanitizeRecallEventDraft,
	type RecallEventDraft,
	type RecallEventDraftErrors,
} from '#app/routes/mocra+/_form+/components/recall-events/types.ts'

type RecallEventOverlayMode = 'add' | 'edit'

type Option = {
	readonly value: string
	readonly label: string
}

type Props = {
	readonly mode: RecallEventOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: RecallEventDraft) => void
	readonly typeOptions: ReadonlyArray<Option>
	readonly outcomeOptions: ReadonlyArray<Option>
	readonly initialValue?: RecallEventDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function RecallEventOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	typeOptions,
	outcomeOptions,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<RecallEventDraft>(
		initialValue ? { ...initialValue } : createRecallEventDraft(),
	)
	const [errors, setErrors] = useState<RecallEventDraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createRecallEventDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [open, onClosed])

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeRecallEventDraft(draft)
		const nextErrors = collectRecallEventErrors(sanitized)
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add' ? RECALL_EVENT_COPY.title.add : RECALL_EVENT_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? RECALL_EVENT_COPY.submit.add
			: RECALL_EVENT_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={RECALL_EVENT_COPY.description}
			className="sm:max-w-xl"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="recall-event-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<RecallEventForm
				formId="recall-event-overlay-form"
				draft={draft}
				errors={errors}
				typeOptions={typeOptions}
				outcomeOptions={outcomeOptions}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
